// Site-wide behaviour: the mobile menu, the bag and the Black Friday banner state.
(() => {
  document.querySelectorAll('[data-mobile-menu]').forEach((menu) => {
    const toggle = menu.querySelector('[data-menu-toggle]');
    const panel = menu.querySelector('[data-menu-panel]');
    if (!toggle || !panel) return;

    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
      panel.hidden = !open;
      document.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || toggle.getAttribute('aria-expanded') !== 'true') return;
      setOpen(false);
      toggle.focus();
    });
    window.matchMedia('(min-width: 961px)').addEventListener('change', (event) => {
      if (event.matches) setOpen(false);
    });
  });

  document.querySelectorAll('[data-country-select]').forEach((select) => {
    select.addEventListener('change', () => select.form.submit());
  });

  // The announcement bar rotates every five seconds, unless the shopper prefers less motion.
  const messages = [...document.querySelectorAll('[data-announcement-message]')];
  if (messages.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let current = 0;
    window.setInterval(() => {
      messages[current].hidden = true;
      current = (current + 1) % messages.length;
      messages[current].hidden = false;
    }, 5000);
  }

  // The bag runs on Shopify's cart API, the same cart the checkout uses.
  const dialog = document.querySelector('[data-bag]');
  const body = dialog?.querySelector('[data-bag-body]');
  const footer = dialog?.querySelector('[data-bag-footer]');
  const subtotal = dialog?.querySelector('[data-bag-subtotal]');
  const status = dialog?.querySelector('[data-bag-status]');
  const discount = dialog?.querySelector('[data-bag-discount]');
  let busy = false;

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  // Follows the store's own money format for the shopper's market, such as €26,95.
  const money = (cents) => {
    const format = window.MartinHurls.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const grouped = amount.replace(/\B(?=(\d{3})+(?!\d))(?=.*\.)/g, ',');
    const comma = amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))(?=.*,)/g, '.');
    const placeholders = {
      amount: grouped,
      amount_no_decimals: grouped.split('.')[0],
      amount_with_comma_separator: comma,
      amount_no_decimals_with_comma_separator: comma.split(',')[0],
    };
    return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => placeholders[key] ?? grouped);
  };

  const lineMarkup = (item, currency) => {
    const properties = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_'));
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    return `
      <li class="bag-line">
        ${item.image ? `<img class="bag-line__image" src="${escapeHtml(item.image)}&width=200" alt="" width="88" height="88" loading="lazy">` : '<span class="bag-line__image"></span>'}
        <div>
          <div class="bag-line__head">
            <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            <span>${money(item.final_line_price, currency)}</span>
          </div>
          ${variant ? `<p class="bag-line__meta">${escapeHtml(variant)}</p>` : ''}
          ${properties.map(([key, value]) => `<p class="bag-line__meta">${escapeHtml(key)}: ${escapeHtml(value)}</p>`).join('')}
          <div class="bag-line__controls">
            <div class="bag-qty" role="group" aria-label="Quantity of ${escapeHtml(item.product_title)}">
              <button type="button" data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button class="bag__text-button" type="button" data-line-key="${escapeHtml(item.key)}" data-quantity="0"
              aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
          </div>
        </div>
      </li>`;
  };

  const render = (cart) => {
    if (!dialog) return;
    if (!cart.items.length) {
      body.innerHTML = `<div class="bag__empty"><p>Your bag is ready when you are.</p></div>`;
      footer.hidden = true;
      return;
    }
    body.innerHTML = `<ul class="bag__lines">${cart.items.map((item) => lineMarkup(item, cart.currency)).join('')}</ul>`;
    subtotal.textContent = money(cart.total_price, cart.currency);
    footer.hidden = false;
  };

  const request = async (url, payload) => {
    const response = await fetch(url, {
      method: payload ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: payload ? JSON.stringify(payload) : undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'The bag could not be updated.');
    return data;
  };

  const refresh = async () => {
    const cart = await request('/cart.js');
    render(cart);
    return cart;
  };

  const open = () => {
    if (dialog && !dialog.open) dialog.showModal();
  };

  const run = async (task) => {
    if (busy) return;
    busy = true;
    dialog.toggleAttribute('data-busy', true);
    status.textContent = '';
    try {
      await task();
    } catch (error) {
      status.textContent = error.message;
    } finally {
      busy = false;
      dialog.removeAttribute('data-busy');
    }
  };

  window.MartinHurlsBag = {
    add: async (items) => {
      await request('/cart/add.js', { items });
      await refresh();
    },
    open,
    refresh,
  };

  if (!dialog) return;

  document.querySelectorAll('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', () => {
      open();
      run(refresh);
    });
  });
  dialog.querySelector('[data-bag-close]')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  body.addEventListener('click', (event) => {
    const button = event.target.closest('[data-line-key]');
    if (!button) return;
    run(async () => {
      const cart = await request('/cart/change.js', { id: button.dataset.lineKey, quantity: Number(button.dataset.quantity) });
      render(cart);
    });
  });

  discount?.addEventListener('submit', (event) => {
    event.preventDefault();
    const input = discount.elements.namedItem('code');
    const code = input.value.trim();
    if (!code) return;
    run(async () => {
      const cart = await request('/cart/update.js', { discount: code });
      const applied = cart.discount_codes?.find((item) => item.code.toLowerCase() === code.toLowerCase());
      if (!applied?.applicable) {
        // Leave the bag as it was rather than keeping a code that does nothing.
        render(await request('/cart/update.js', { discount: '' }));
        throw new Error(`The code “${code}” can’t be used with this bag.`);
      }
      render(cart);
      input.value = '';
    });
  });
})();
